// Python (.py) and Jupyter (.ipynb) export for Natural Math Editor Canvas LTI
// Takes a LaTeX string (from mathmlToLatex) and triggers a file download.

function triggerDownload(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Escape a LaTeX string for embedding inside a Python double-quoted string. */
function escapePy(latex: string): string {
  return latex.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Download a Python script (.py) that parses the equation with SymPy.
 * Requirements for the end user: pip install sympy lark
 */
export function exportAsPython(latex: string): void {
  const escaped = escapePy(latex);
  const lines = [
    '# Generated by Natural Math Editor -- naturalmatheditor.com',
    '# Requirements: pip install sympy lark',
    'from sympy import *',
    'from sympy.parsing.latex import parse_latex',
    '',
    `latex_str = "${escaped}"`,
    'print("LaTeX:", latex_str)',
    '',
    'try:',
    '    result = parse_latex(latex_str, backend="lark")',
    '    if hasattr(result, "data") and result.data == "_ambig":',
    '        print("Note: multiple interpretations found, using first")',
    '        expr = result.children[0]',
    '    else:',
    '        expr = result',
    '    print("Parsed:", expr)',
    '    try:',
    '        print("Simplified:", simplify(expr))',
    '    except Exception:',
    '        print("Simplified: (could not simplify further)")',
    'except Exception as e:',
    '    print("Could not parse expression:", e)',
    '    print("The LaTeX above is still valid -- paste it into a SymPy session manually.")',
  ];
  triggerDownload(lines.join('\n'), 'equation.py', 'text/x-python');
}

/**
 * Download a Jupyter notebook (.ipynb) with the equation rendered as LaTeX
 * and SymPy code ready to run.
 * Requirements for the end user: pip install sympy lark
 */
export function exportAsJupyter(latex: string): void {
  const escaped = escapePy(latex);
  const notebook = {
    nbformat: 4,
    nbformat_minor: 5,
    metadata: {
      kernelspec: {
        display_name: 'Python 3',
        language: 'python',
        name: 'python3',
      },
      language_info: { name: 'python', version: '3.x' },
    },
    cells: [
      {
        cell_type: 'markdown',
        metadata: {},
        source: [
          '## Equation from Natural Math Editor\n',
          `$${latex}$`,
        ],
      },
      {
        cell_type: 'code',
        execution_count: null,
        metadata: {},
        outputs: [],
        source: [
          'from sympy import *\n',
          'from sympy.parsing.latex import parse_latex\n',
          '\n',
          `# LaTeX: ${latex}\n`,
          `result = parse_latex("${escaped}", backend="lark")\n`,
          'expr = result.children[0] if hasattr(result, "data") and result.data == "_ambig" else result\n',
          'expr',
        ],
      },
      {
        cell_type: 'code',
        execution_count: null,
        metadata: {},
        outputs: [],
        source: ['simplify(expr)'],
      },
    ],
  };
  triggerDownload(
    JSON.stringify(notebook, null, 2),
    'equation.ipynb',
    'application/x-ipynb+json',
  );
}
